import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/api';
import { StatCard, Card, BarChart, DetailRow, PeriodPicker, EL_MONTH_NAMES } from '../components/Shared';
import toast from 'react-hot-toast';
import styles from './Dashboard.module.css';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // @period-picker-v1 — only the two "μήνα" cards and the charts below
  // follow this. Doctor/patient totals and the Σήμερα card are always now.
  const _now = new Date();
  const [period, setPeriod] = useState({ year: _now.getFullYear(), month: _now.getMonth() + 1 });

  useEffect(() => {
    setLoading(true);
    dashboardAPI.getStats({ year: period.year, month: period.month })
      .then(res => setStats(res.data.data))
      .catch(() => toast.error('Αδυναμία φόρτωσης dashboard'))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className={styles.loading}>Φόρτωση...</div>;

  const s = stats || {};
  const topDoctors = s.topDoctors || [];
  const byService = s.byService || [];
  const periodLabel = `${EL_MONTH_NAMES[period.month - 1]} ${period.year}`;

  // Build today activity summary
  const todayTotal = s.todayTotal || 0;
  const todayScheduled = s.todayScheduled || 0;
  const todayCompleted = s.todayCompleted || 0;
  const todayInProgress = s.todayInProgress || 0;
  const todayCancelled = s.todayCancelled || 0;

  return (
    <div className={styles.page}>

      {/* @period-picker-v1 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <PeriodPicker
          year={period.year}
          month={period.month}
          onChange={(year, month) => setPeriod({ year, month })}
        />
      </div>

      {/* Stats row */}
      <div className={styles.statsGrid}>
        <StatCard icon="🩺" iconBg="rgba(192,24,90,0.1)" value={s.activeDoctors || 0} label="Ενεργοί ιατροί" />
        <StatCard icon="👥" iconBg="rgba(15,31,61,0.07)" value={s.totalPatients || 0} label="Εγγεγραμμένοι ασθενείς" />
        <StatCard icon="✅" iconBg="rgba(13,154,106,0.1)" value={s.monthConsultations || 0} label={`Διαβουλεύσεις — ${periodLabel}`} />
        <StatCard icon="💶" iconBg="rgba(224,123,42,0.1)" value={`€${(s.monthRevenue || 0).toLocaleString()}`} label={`Έσοδα — ${periodLabel}`} />
        {/* @admin-refunds-card-v1 (30 Sep 2026) -- M-52. monthRefunds has been
            in the dashboard payload all along with nothing rendering it, so a
            month's refunds sat invisible next to its revenue. */}
        <StatCard icon="↩️" iconBg="rgba(220,38,38,0.1)" value={`€${(s.monthRefunds || 0).toLocaleString()}`} label={`Επιστροφές — ${periodLabel}`} />
      </div>

      <div className={styles.mainGrid}>

        {/* Today's activity — count summary */}
        <Card
          title={`Δραστηριότητα Σήμερα — ${new Date().toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
          action={<span onClick={() => navigate('/rota')} style={{ cursor: 'pointer' }}>Προβολή ρόστερ →</span>}
        >
          {todayTotal === 0 ? (
            <div style={{ padding: '40px 28px', textAlign: 'center', color: 'var(--muted)' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
              <div>Δεν υπάρχουν ραντεβού σήμερα</div>
            </div>
          ) : (
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 42, fontWeight: 700, color: 'var(--navy)', textAlign: 'center', marginBottom: 6 }}>
                {todayTotal}
              </div>
              <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, marginBottom: 20 }}>
                {todayTotal === 1 ? 'ραντεβού σήμερα' : 'ραντεβού σήμερα'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                <div style={{ padding: 14, background: 'rgba(192,24,90,0.08)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#C0185A' }}>{todayScheduled}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Προγραμματισμένα</div>
                </div>
                <div style={{ padding: 14, background: 'rgba(13,154,106,0.08)', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#0D9A6A' }}>{todayCompleted}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Ολοκληρωμένα</div>
                </div>
                {todayInProgress > 0 && (
                  <div style={{ padding: 14, background: 'rgba(224,123,42,0.08)', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#E07B2A' }}>{todayInProgress}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Σε εξέλιξη</div>
                  </div>
                )}
                {todayCancelled > 0 && (
                  <div style={{ padding: 14, background: 'rgba(138,151,168,0.12)', borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#8A97A8' }}>{todayCancelled}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Ακυρωμένα</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* @dashboard-today-async-v1 (20 Oct 2026): repeat prescriptions and sick notes, which are not
              appointments and so never appear in the counts above. Received and completed use Athens dates;
              "Σε αναμονή" is what is still in a doctor's queue right now. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, padding: '0 20px 20px' }}>
            {[
              ['💊 Επαναλήψεις συνταγών', s.todayRenewals],
              ['📝 Αναρρωτικές άδειες', s.todaySickLeave],
            ].map(([title, v]) => (
              <div key={title} style={{ padding: 14, background: 'rgba(15,31,61,0.04)', borderRadius: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>{title}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, textAlign: 'center' }}>
                  {[['Νέα σήμερα', v && v.new, '#C0185A'], ['Ολοκληρώθηκαν', v && v.done, '#0D9A6A'], ['Σε αναμονή', v && v.waiting, '#E07B2A']].map(([label, n, color]) => (
                    <div key={label}>
                      <div style={{ fontSize: 20, fontWeight: 700, color }}>{n || 0}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Right column */}
        <div>
          <Card title="Εκκρεμείς Ενέργειες">
            <div onClick={() => navigate('/doctors/pending')} style={{ cursor: 'pointer' }}>
              <DetailRow label="⏳ Ιατροί αναμένουν έγκριση" value={<span style={{ color: 'var(--danger)' }}>{s.pendingDoctors || 0}</span>} />
            </div>
            <div onClick={() => navigate('/slots/pending')} style={{ cursor: 'pointer' }}>
              <DetailRow label="📅 Slots προς δημοσίευση" value={<span style={{ color: 'var(--warning)' }}>{s.pendingSlots || 0}</span>} />
            </div>
            <DetailRow label="❌ Ακυρώσεις σήμερα" value={s.todayCancellations || 0} />
          </Card>
        </div>
      </div>

      {/* Bottom row */}
      <div className={styles.bottomGrid}>
        <Card title="Κορυφαίοι Ιατροί (Μήνας)">
          {topDoctors.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              Δεν υπάρχουν ολοκληρωμένες διαβουλεύσεις αυτόν τον μήνα
            </div>
          ) : (
            <BarChart data={topDoctors.map(d => ({ label: `Δρ. ${d.lastName}`, value: d.count }))} />
          )}
        </Card>
        <Card title="Ανά Υπηρεσία">
          {byService.length === 0 ? (
            <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              Δεν υπάρχουν ολοκληρωμένες διαβουλεύσεις αυτόν τον μήνα
            </div>
          ) : (
            <BarChart data={byService.map(item => ({ label: item.name, value: item.count }))} />
          )}
        </Card>
        <Card title="Σύνοψη Εσόδων">
          <div className={styles.revenueGrid}>
            <div className={styles.revItem}>
              <div className={styles.revVal}>€{(s.monthRevenue || 0).toLocaleString()}</div>
              <div className={styles.revLabel}>Ακαθάριστα έσοδα</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
